import { type IProjectBus, ProjectBusEvents, TOKEN_IProjectBus } from "@/services/ProjectBus";
import { TOKEN_IProjectSyncing, type IProjectSyncing } from "@/services/ProjectSyncing";
import { TOKEN_IProjectWorldReader, type IProjectWorldReader } from "@/services/ProjectWorldReader";
import { TOKEN_IRealtime, type IRealtime } from "@/services/Realtime";
import { TOKEN_IWorldBuilder, type IWorldBuilder } from "@/services/WorldBuilder";
import { makeAutoObservable } from "mobx";
import { inject, injectable } from "tsyringe";

export const TOKEN_ReplayStore = Symbol("ReplayStore");

@injectable()
export class ReplayStore {
    private _requestCancelReplay: boolean = false;
    private _eventPosition: number | undefined = undefined;
    private _numberOfEvents: number | undefined = undefined;
    private _projectId: string | undefined = undefined;

    get isInHistoryMode(): boolean {
        return this._eventPosition !== undefined;
    }
    public get eventPercentage(): number | undefined {
        if (this._eventPosition === undefined || this._numberOfEvents === undefined) return undefined;
        if (this._numberOfEvents === 0) return undefined; // Avoid division by zero
        return this._eventPosition / this._numberOfEvents * 100;
    }

    constructor(
        @inject(TOKEN_IWorldBuilder) private worldBuilder: IWorldBuilder,
        @inject(TOKEN_IProjectWorldReader) private projectWorldReader: IProjectWorldReader,
        @inject(TOKEN_IRealtime) private realtime: IRealtime,
        @inject(TOKEN_IProjectSyncing) private projectSyncing: IProjectSyncing,
        @inject(TOKEN_IProjectBus) projectBus: IProjectBus,
    ) {
        // If a replay is in progress, stop it when the project is loaded
        projectBus.subscribe_projectLoaded(this.onProjectLoaded);
        projectBus.subscribe_projectMetadataUpdated(this.projectMetadataUpdated);

        makeAutoObservable(this);
    }
    private projectMetadataUpdated = async (data: ProjectBusEvents['PROJECT_METADATA_UPDATED']) => {
        if (data.projectMetadata.id !== this._projectId) return;
        this._numberOfEvents = data.projectMetadata.numberOfEvents;
    }

    private onProjectLoaded = async (data: ProjectBusEvents['PROJECT_LOADED']) => {
        this.stopReplay();
        this._projectId = data.projectId;
        const projectWorld = await this.projectWorldReader.mustGet(data.projectId);
        this._numberOfEvents = projectWorld.projectMetadata.numberOfEvents;
    }

    replayEvents = async () => {
        this.realtime.disconnectAll();
        this._requestCancelReplay = false;
        this._eventPosition = 0;
        await this.worldBuilder.iterativeHistoryPlay(this._projectId!,
            (position) => {
                this._eventPosition = position;
                return this._requestCancelReplay
            },
        );
        this._eventPosition = undefined;
        this._requestCancelReplay = false;
        this.projectSyncing.readFromServer(this._projectId!);
    }

    stopReplay = () => {
        this._requestCancelReplay = true;
    }
}